import { InstallWrapper } from "@/components/InstallWrapper";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/auth/useAuth";
import { useNotifications } from "@/hooks/useNotifications";
import { useProfileQuery } from "@/queries/profile";
import { ArrowLeft, Edit, Key, LoaderCircle, LogOut } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const Account = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const notifications = useNotifications();

  const userId = user?.id;
  const { data: profile } = useProfileQuery(userId);

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  const getInitial = (email?: string | null) => {
    if (!email) return "?";
    return email.charAt(0).toUpperCase();
  };

  return (
    <div className="gradient-bg flex min-h-full flex-col items-center justify-start px-4 py-6">
      <div className="mx-auto w-full max-w-lg">
        {/* Header with back button and title */}
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            className="p-2"
            onClick={() => navigate(-1)}
            aria-label="Retour"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-gray-700">Mon compte</h1>
        </div>

        {/* Profile section with avatar, email, and edit button */}
        <div className="mb-6 flex w-full items-start gap-4 rounded-xl bg-white px-5 py-4 shadow-sm">
          <Avatar className="mt-1 h-12 w-12 shadow-sm">
            <AvatarFallback className="bg-indigo-100 text-xl text-indigo-700">
              {getInitial(user?.email)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className="truncate text-lg font-semibold text-gray-900">
                {profile?.full_name || user?.email || "Utilisateur inconnu"}
              </h3>
              <Button variant="ghost" size="icon" asChild>
                <Link to="/profile" aria-label="Modifier le profil">
                  <Edit className="ml-auto h-5 w-5" />
                </Link>
              </Button>
            </div>
            {profile?.bio && (
              <p className="text-sm text-gray-500">{profile.bio}</p>
            )}
          </div>
        </div>

        {/* Notification Switch - moved to top */}
        <div className="mb-6 flex w-full flex-col space-y-3 rounded-xl bg-white px-5 py-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <Switch
              id="notifications-switch"
              checked={notifications.isEnabled}
              onCheckedChange={notifications.toggle}
              disabled={
                !notifications.canEnable ||
                notifications.isLoading ||
                notifications.isUpdating
              }
              aria-label="Activer les notifications sur cet appareil"
            />
            <Label
              htmlFor="notifications-switch"
              className="flex-1 text-base font-medium text-gray-900"
            >
              Notifications sur cet appareil
            </Label>
            {(notifications.isLoading || notifications.isUpdating) && (
              <LoaderCircle className="text-primary h-6 w-6 animate-spin" />
            )}
          </div>
          {!notifications.canEnable && (
            <div className="text-sm text-gray-500">
              <p>
                Veuillez installer l'application pour activer les notifications
              </p>
              <InstallWrapper>
                <Button variant="link" className="p-0">
                  Comment installer ?
                </Button>
              </InstallWrapper>
            </div>
          )}
        </div>

        {/* List-style actions for account management */}
        <div className="divide-y divide-gray-200 overflow-hidden rounded-xl bg-white shadow-sm">
          {/* Reset Password List Item */}
          <Link
            to="/change-password"
            className="aria-label='Changer le mot de passe' flex w-full items-center gap-3 px-5 py-4 text-left text-base font-medium text-gray-900 transition-colors hover:bg-gray-50 focus:bg-gray-100"
          >
            <Key className="h-5 w-5 text-indigo-600" />
            <span className="flex-1">Changer le mot de passe</span>
          </Link>
          {/* Logout List Item with Confirmation */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className="aria-label='Se déconnecter' flex w-full items-center gap-3 px-5 py-4 text-left text-base font-medium text-red-600 transition-colors hover:bg-gray-50 focus:bg-gray-100"
                type="button"
              >
                <LogOut className="h-5 w-5" />
                <span className="flex-1">Se déconnecter</span>
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-base">
                  Se déconnecter&nbsp;?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Voulez-vous vraiment vous déconnecter&nbsp;? Vous pourrez vous
                  reconnecter à tout moment.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={handleLogout} className="">
                  Se déconnecter
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};

export default Account;
